import React, { useEffect, useMemo, useState } from 'react';
import { getTenantToday } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  DownloadCloud,
  Database,
  Loader2,
  Wand2,
} from 'lucide-react';
import {
  ActionBar,
  DataTable,
  EmptyStateBox,
  InfoCard,
  InfoGrid,
  InfoLabel,
  InfoValue,
  PanelBody,
  PanelHeader,
  PanelSubtitle,
  PanelTitle,
  StatusPill,
  TableShell,
  WorkspaceButton,
  WorkspacePanel,
  numberCompact,
  percent,
  type Tone,
} from './shared/CreditWorkspace';
import { axiomTheme, toneAlpha, toneColor } from '../../styles/designTokens';

import { DateField } from '../shared/DateField';

type AssetClass = 'PRIVATE_CREDIT' | 'LEVERAGED_LOAN';
type BookingMode = 'STAGE_ONLY' | 'CREATE_DRAFTS';

interface Props {
  onImported?: () => void;
}

const ImportShell = styled(WorkspacePanel)<{ $expanded?: boolean }>`
  border-color: rgba(56, 189, 248, 0.22);
  background:
    linear-gradient(135deg, rgba(8, 47, 73, 0.28), rgba(15, 23, 42, 0.68)),
    ${axiomTheme.surface.panel};

  ${PanelHeader} {
    border-bottom: ${p => p.$expanded ? `1px solid ${axiomTheme.color.border.subtle}` : '0'};
  }
`;

const ImportGrid = styled.div`
  display: grid;
  grid-template-columns: 340px minmax(0, 1fr);
  gap: 1rem;

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;
  }
`;

const FieldStack = styled.div`
  display: grid;
  gap: 0.72rem;
`;

const Field = styled.label`
  display: grid;
  gap: 0.35rem;
  color: ${axiomTheme.color.text.muted};
  font-size: 0.68rem;
  font-weight: 850;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  input,
  select,
  textarea {
    border: 1px solid ${axiomTheme.color.border.default};
    border-radius: 8px;
    background: rgba(2, 6, 23, 0.54);
    color: ${axiomTheme.color.text.primary};
    font-size: 0.82rem;
    font-weight: 700;
    letter-spacing: 0;
    padding: 0.72rem 0.78rem;
    outline: none;
  }

  textarea {
    min-height: 188px;
    resize: vertical;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.74rem;
    line-height: 1.45;
  }

  input:focus,
  select:focus,
  textarea:focus {
    border-color: ${axiomTheme.color.border.focus};
    box-shadow: 0 0 0 2px rgba(56, 189, 248, 0.12);
  }
`;

const DropZone = styled.div`
  border: 1px dashed rgba(56, 189, 248, 0.46);
  border-radius: 12px;
  background: rgba(8, 47, 73, 0.22);
  padding: 0.78rem;

  input {
    width: 100%;
    color: ${axiomTheme.color.text.muted};
  }
`;

const MappingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.7rem;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const MiniCard = styled.div<{ $tone?: Tone }>`
  border: 1px solid ${p => toneAlpha(p.$tone || 'slate', '36')};
  border-left: 3px solid ${p => toneColor(p.$tone || 'slate')};
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.58);
  padding: 0.76rem 0.82rem;
`;

const MiniLabel = styled.div`
  color: ${axiomTheme.color.text.faint};
  font-size: 0.62rem;
  font-weight: 850;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const MiniValue = styled.div`
  color: ${axiomTheme.color.text.primary};
  font-size: 1rem;
  font-weight: 900;
  margin-top: 0.22rem;
`;

const Notice = styled.div<{ $tone?: Tone }>`
  border: 1px solid ${p => toneAlpha(p.$tone || 'cyan', '36')};
  border-radius: 8px;
  background: ${p => toneAlpha(p.$tone || 'cyan', '14')};
  color: ${axiomTheme.color.text.secondary};
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr);
  gap: 0.55rem;
  padding: 0.72rem 0.8rem;
  font-size: 0.76rem;
  line-height: 1.4;

  svg {
    width: 16px;
    height: 16px;
    color: ${p => toneColor(p.$tone || 'cyan')};
    margin-top: 1px;
  }
`;

const InlineCode = styled.code`
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 999px;
  background: rgba(2, 6, 23, 0.58);
  color: #bae6fd;
  display: inline-flex;
  font-size: 0.68rem;
  font-weight: 800;
  padding: 0.16rem 0.44rem;
`;

const splitCsvLine = (line: string) => {
  const out: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      out.push(cell.trim());
      cell = '';
    } else {
      cell += ch;
    }
  }
  out.push(cell.trim());
  return out;
};

const summarizeCsv = (csv: string) => {
  const lines = csv.split(/\r?\n/).filter(line => line.trim().length > 0);
  const headers = lines.length ? splitCsvLine(lines[0]) : [];
  const rows = lines.slice(1, 4).map(splitCsvLine);
  return { headers, rows, rowCount: Math.max(0, lines.length - 1) };
};

const toneForStatus = (status?: string): Tone => {
  const s = String(status || '').toUpperCase();
  if (['COMPLETED', 'STAGED', 'MAPPED'].includes(s)) return 'emerald';
  if (['PARTIAL', 'NEEDS_REVIEW', 'PROCESSING'].includes(s)) return 'amber';
  if (['FAILED', 'REJECTED'].includes(s)) return 'red';
  return 'slate';
};

const todayIso = () => getTenantToday();

const CreditFileImportPanel: React.FC<Props> = ({ onImported }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [assetClass, setAssetClass] = useState<AssetClass>('LEVERAGED_LOAN');
  const [fileType, setFileType] = useState('LL_AGENT_BANK_FILE');
  const [provider, setProvider] = useState('Agent Bank / WSO');
  const [businessDate, setBusinessDate] = useState(todayIso());
  const [sourceFilename, setSourceFilename] = useState('credit-file.csv');
  const [bookingMode, setBookingMode] = useState<BookingMode>('STAGE_ONLY');
  const [csvContent, setCsvContent] = useState('');
  const [preview, setPreview] = useState<any>(null);
  const [lastRun, setLastRun] = useState<any>(null);
  const [runs, setRuns] = useState<any[]>([]);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState<{ tone: Tone; message: string } | null>(null);

  const csvSummary = useMemo(() => summarizeCsv(csvContent), [csvContent]);
  const mapping = preview?.proposedMapping || {};
  const mappingRows = Object.entries(mapping);

  const loadRuns = async () => {
    try {
      const response = await axios.get('/api/credit/file-imports', {
        params: { limit: 6, _: Date.now() },
      });
      setRuns(Array.isArray(response.data) ? response.data : []);
    } catch {
      setRuns([]);
    }
  };

  useEffect(() => {
    loadRuns();
  }, []);

  useEffect(() => {
    if (assetClass === 'LEVERAGED_LOAN') {
      setFileType('LL_AGENT_BANK_FILE');
      setProvider('Agent Bank / WSO');
    } else {
      setFileType('PRIVATE_CREDIT_ADMIN_FILE');
      setProvider('Fund Admin / Borrower');
    }
    setPreview(null);
    setLastRun(null);
  }, [assetClass]);

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setSourceFilename(file.name);
      setCsvContent(text);
      setPreview(null);
      setLastRun(null);
      setNotice({ tone: 'slate', message: `${file.name}: ${numberCompact(Math.max(0, text.split(/\r?\n/).filter(Boolean).length - 1))} rows loaded.` });
    } finally {
      event.target.value = '';
    }
  };

  const payload = () => ({
    assetClass,
    fileType,
    provider,
    businessDate,
    sourceFilename,
    csvContent,
    mapping: preview?.proposedMapping || undefined,
    bookingMode,
  });

  const previewFile = async () => {
    if (!csvContent.trim()) {
      setNotice({ tone: 'amber', message: 'Load a CSV before mapping.' });
      return;
    }
    setBusy('preview');
    setNotice(null);
    try {
      const response = await axios.post('/api/credit/file-imports/preview', payload());
      setPreview(response.data || null);
      setNotice({
        tone: toneForStatus(response.data?.status),
        message: `${response.data?.status || 'MAPPED'}: ${numberCompact(response.data?.rowCount || 0)} rows, ${numberCompact(Object.keys(response.data?.proposedMapping || {}).length)} mapped fields.`,
      });
    } catch (e: any) {
      setNotice({ tone: 'red', message: e?.response?.data?.error || e?.message || 'Mapping failed.' });
    } finally {
      setBusy('');
    }
  };

  const ingestFile = async () => {
    if (!csvContent.trim()) {
      setNotice({ tone: 'amber', message: 'Load a CSV before ingest.' });
      return;
    }
    setBusy('ingest');
    setNotice(null);
    try {
      const response = await axios.post('/api/credit/file-imports/ingest', payload());
      setLastRun(response.data || null);
      setNotice({
        tone: toneForStatus(response.data?.status),
        message: response.data?.message || `Import ${response.data?.status || 'submitted'}.`,
      });
      setCsvContent('');
      setPreview(null);
      await loadRuns();
      onImported?.();
      // A CREATE_DRAFTS ingest also lands on the blotter with its new DRAFT tickets.
      if (bookingMode === 'CREATE_DRAFTS'
          && (response.data?.rows_booked ?? response.data?.rowsBooked ?? 0) > 0) {
        navigate('/bond-transactions?status=DRAFT');
      }
    } catch (e: any) {
      setNotice({ tone: 'red', message: e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Ingest failed.' });
    } finally {
      setBusy('');
    }
  };

  const syncDealCloud = async () => {
    setBusy('dealcloud');
    setNotice(null);
    try {
      const response = await axios.post('/api/integrations/dealcloud/sync');
      const imported = response.data?.import || null;
      setLastRun(imported);
      setNotice({
        tone: toneForStatus(imported?.status || 'STAGED'),
        // F-MK-083: an unchanged deal is not staged again, so a sync may stage nothing.
        message: imported?.message || response.data?.message
          || `DealCloud: ${response.data?.dealsFound ?? 0} closed deal(s) found.`,
      });
      await loadRuns();
    } catch (e: any) {
      setNotice({ tone: 'red', message: e?.response?.data?.error || e?.message || 'DealCloud sync failed.' });
    } finally {
      setBusy('');
    }
  };

  const bookStagedRun = async (runId: number) => {
    setBusy(`book-${runId}`);
    setNotice(null);
    try {
      const response = await axios.post(`/api/credit/file-imports/${runId}/book`);
      setLastRun(response.data || null);
      setNotice({
        tone: toneForStatus(response.data?.status),
        message: response.data?.message || `Run #${runId} booked.`,
      });
      await loadRuns();
      onImported?.();
      // Land on the blotter showing the DRAFT tickets this booking just created.
      if ((response.data?.rows_booked ?? response.data?.rowsBooked ?? 0) > 0) {
        navigate('/bond-transactions?status=DRAFT');
      }
    } catch (e: any) {
      setNotice({ tone: 'red', message: e?.response?.data?.error || e?.message || 'Booking staged run failed.' });
    } finally {
      setBusy('');
    }
  };

  return (
    <ImportShell $expanded={expanded}>
      <PanelHeader>
        <div>
          <PanelTitle>Credit File Import</PanelTitle>
          <PanelSubtitle>PC and syndicated-loan file staging with optional draft acquisition flow creation.</PanelSubtitle>
        </div>
        <ActionBar>
          {expanded ? (
            <>
              <WorkspaceButton onClick={syncDealCloud} disabled={busy === 'dealcloud'}>
                {busy === 'dealcloud' ? <Loader2 size={14} /> : <DownloadCloud size={14} />}
                {busy === 'dealcloud' ? 'Pulling...' : 'Pull from DealCloud'}
              </WorkspaceButton>
              <WorkspaceButton onClick={previewFile} disabled={busy === 'preview' || !csvContent.trim()}>
                {busy === 'preview' ? <Loader2 size={14} /> : <Wand2 size={14} />}
                {busy === 'preview' ? 'Mapping...' : 'Infer mapping'}
              </WorkspaceButton>
              <WorkspaceButton $variant="primary" onClick={ingestFile} disabled={busy === 'ingest' || !csvContent.trim()}>
                {busy === 'ingest' ? <Loader2 size={14} /> : <Database size={14} />}
                {busy === 'ingest' ? 'Ingesting...' : bookingMode === 'CREATE_DRAFTS' ? 'Create drafts' : 'Stage file'}
              </WorkspaceButton>
              <WorkspaceButton onClick={() => setExpanded(false)} aria-expanded={expanded}>
                <ChevronUp size={14} />
                Collapse
              </WorkspaceButton>
            </>
          ) : (
            <WorkspaceButton $variant="primary" onClick={() => setExpanded(true)} aria-expanded={expanded}>
              <ChevronDown size={14} />
              Enlarge import
            </WorkspaceButton>
          )}
        </ActionBar>
      </PanelHeader>
      {expanded && (
      <PanelBody>
        {notice && (
          <Notice $tone={notice.tone} style={{ marginBottom: '0.9rem' }}>
            {notice.tone === 'red' ? <AlertTriangle /> : <CheckCircle2 />}
            <span>{notice.message}</span>
          </Notice>
        )}

        <ImportGrid>
          <FieldStack>
            <Field>
              Asset class
              <select value={assetClass} onChange={e => setAssetClass(e.target.value as AssetClass)}>
                <option value="LEVERAGED_LOAN">Leveraged Loan</option>
                <option value="PRIVATE_CREDIT">Private Credit</option>
              </select>
            </Field>
            <Field>
              File type
              <select value={fileType} onChange={e => setFileType(e.target.value)}>
                {assetClass === 'LEVERAGED_LOAN' ? (
                  <>
                    <option value="LL_AGENT_BANK_FILE">Agent bank / WSO position file</option>
                    <option value="LL_ACTIVITY_FILE">Loan activity file</option>
                  </>
                ) : (
                  <>
                    <option value="PRIVATE_CREDIT_ADMIN_FILE">Fund admin position file</option>
                    <option value="PRIVATE_CREDIT_BORROWER_REPORTING_FILE">Borrower reporting file</option>
                  </>
                )}
              </select>
            </Field>
            <Field>
              Booking mode
              <select value={bookingMode} onChange={e => setBookingMode(e.target.value as BookingMode)}>
                <option value="STAGE_ONLY">Stage and validate</option>
                <option value="CREATE_DRAFTS">Create acquisition drafts</option>
              </select>
            </Field>
            <Field>
              Provider
              <input value={provider} onChange={e => setProvider(e.target.value)} />
            </Field>
            <Field>
              Business date
              <DateField value={businessDate ?? ''} onChange={iso => setBusinessDate(iso)} />
            </Field>
            <Field>
              Source filename
              <input value={sourceFilename} onChange={e => setSourceFilename(e.target.value)} />
            </Field>
            <DropZone>
              <input type="file" accept=".csv,.txt,text/csv,text/plain" onChange={handleFile} />
            </DropZone>
          </FieldStack>

          <FieldStack>
            <Field>
              CSV content
              <textarea
                value={csvContent}
                onChange={e => {
                  setCsvContent(e.target.value);
                  setPreview(null);
                  setLastRun(null);
                }}
                placeholder={assetClass === 'LEVERAGED_LOAN'
                  ? 'loan_id,borrower,facility,funded_amount,price,spread_bps,maturity_date'
                  : 'loan_id,borrower,sponsor,facility_type,commitment,spread_bps,maturity_date'}
              />
            </Field>

            <MappingGrid>
              <MiniCard $tone={csvSummary.rowCount ? 'cyan' : 'slate'}>
                <MiniLabel>Rows</MiniLabel>
                <MiniValue>{numberCompact(csvSummary.rowCount)}</MiniValue>
              </MiniCard>
              <MiniCard $tone={csvSummary.headers.length ? 'cyan' : 'slate'}>
                <MiniLabel>Columns</MiniLabel>
                <MiniValue>{numberCompact(csvSummary.headers.length)}</MiniValue>
              </MiniCard>
              <MiniCard $tone={toneForStatus(preview?.status)}>
                <MiniLabel>Mapping</MiniLabel>
                <MiniValue>{preview?.confidenceScore != null ? percent(Number(preview.confidenceScore) * 100) : '--'}</MiniValue>
              </MiniCard>
            </MappingGrid>

            {csvSummary.headers.length > 0 && (
              <InfoGrid>
                <InfoCard>
                  <InfoLabel>Source columns</InfoLabel>
                  <InfoValue>
                    {csvSummary.headers.slice(0, 10).map(header => (
                      <InlineCode key={header} style={{ marginRight: 5, marginBottom: 5 }}>{header}</InlineCode>
                    ))}
                  </InfoValue>
                </InfoCard>
              </InfoGrid>
            )}
          </FieldStack>
        </ImportGrid>

        {mappingRows.length > 0 && (
          <TableShell style={{ marginTop: '1rem' }}>
            <DataTable>
              <thead>
                <tr>
                  <th>Canonical Field</th>
                  <th>Source Column</th>
                </tr>
              </thead>
              <tbody>
                {mappingRows.slice(0, 18).map(([field, source]) => (
                  <tr key={field}>
                    <td>{field}</td>
                    <td><InlineCode>{String(source)}</InlineCode></td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </TableShell>
        )}

        {lastRun && (
          <InfoGrid style={{ marginTop: '1rem' }}>
            <InfoCard><InfoLabel>Last run</InfoLabel><InfoValue>#{lastRun.id || lastRun.ID}</InfoValue></InfoCard>
            <InfoCard><InfoLabel>Status</InfoLabel><InfoValue><StatusPill $tone={toneForStatus(lastRun.status)}>{lastRun.status}</StatusPill></InfoValue></InfoCard>
            <InfoCard><InfoLabel>Loaded</InfoLabel><InfoValue>{numberCompact(lastRun.rows_loaded ?? lastRun.rowsLoaded)}</InfoValue></InfoCard>
            <InfoCard><InfoLabel>Rejected</InfoLabel><InfoValue>{numberCompact(lastRun.rows_rejected ?? lastRun.rowsRejected)}</InfoValue></InfoCard>
            <InfoCard><InfoLabel>Booked</InfoLabel><InfoValue>{numberCompact(lastRun.rows_booked ?? lastRun.rowsBooked)}</InfoValue></InfoCard>
          </InfoGrid>
        )}

        <div style={{ marginTop: '1rem' }}>
          {runs.length === 0 ? (
            <EmptyStateBox>No credit file import runs yet.</EmptyStateBox>
          ) : (
            <TableShell>
              <DataTable>
                <thead>
                  <tr>
                    <th>Run</th>
                    <th>Asset</th>
                    <th>File</th>
                    <th>Status</th>
                    <th>Rows</th>
                    <th>Rejected</th>
                    <th>Business Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map(run => (
                    <tr key={run.id}>
                      <td>#{run.id}</td>
                      <td>{run.asset_class || run.assetClass}</td>
                      <td>{run.source_filename || run.sourceFilename || run.file_type || run.fileType}</td>
                      <td><StatusPill $tone={toneForStatus(run.status)}>{run.status}</StatusPill></td>
                      <td>{numberCompact(run.rows_loaded ?? run.rowsLoaded ?? 0)} / {numberCompact(run.rows_received ?? run.rowsReceived ?? 0)}</td>
                      <td>{numberCompact(run.rows_rejected ?? run.rowsRejected ?? 0)}</td>
                      <td>{run.business_date || run.businessDate || '--'}</td>
                      <td>
                        {String(run.status).toUpperCase() === 'STAGED' ? (
                          <WorkspaceButton
                            $variant="primary"
                            onClick={() => bookStagedRun(run.id)}
                            disabled={busy === `book-${run.id}`}
                          >
                            {busy === `book-${run.id}` ? <Loader2 size={13} /> : <Database size={13} />}
                            {busy === `book-${run.id}` ? 'Booking...' : 'Create drafts'}
                          </WorkspaceButton>
                        ) : '--'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </TableShell>
          )}
        </div>
      </PanelBody>
      )}
    </ImportShell>
  );
};

export default CreditFileImportPanel;
